import { Metadata } from 'next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
}

/**
 * SEO 组件 - 用于生成页面的 SEO 元数据
 * 返回 Next.js Metadata 对象
 */
export function generateSEOMetadata({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
}: SEOProps): Metadata {
  const siteName = '企业官网';
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3002';
  const fullTitle = title ? `${title} - ${siteName}` : siteName;
  const fullDescription = description || '企业官网 - 提供优质的产品和服务';
  const fullImage = image || `${siteUrl}/og-image.jpg`;
  const fullUrl = url || siteUrl;

  return {
    title: fullTitle,
    description: fullDescription,
    keywords: keywords,
    authors: [{ name: siteName }],
    openGraph: {
      title: fullTitle,
      description: fullDescription,
      url: fullUrl,
      siteName: siteName,
      images: [
        {
          url: fullImage,
          width: 1200,
          height: 630,
          alt: title || siteName,
        },
      ],
      type: type,
      locale: 'zh_CN',
    },
    twitter: {
      card: 'summary_large_image',
      title: fullTitle,
      description: fullDescription,
      images: [fullImage],
    },
    robots: {
      index: !noindex,
      follow: !noindex,
      googleBot: {
        index: !noindex,
        follow: !noindex,
      },
    },
    alternates: {
      canonical: fullUrl,
    },
  };
}

/**
 * 结构化数据组件 - 用于生成 JSON-LD 结构化数据
 */
export function StructuredData({
  type,
  data,
}: {
  type: 'Organization' | 'WebSite' | 'BreadcrumbList' | 'Article' | 'Product';
  data: any;
}) {
  const getStructuredData = () => {
    const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3002';

    switch (type) {
      case 'Organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: data.name || '企业官网',
          url: baseUrl,
          logo: data.logo || `${baseUrl}/logo.png`,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: data.phone || '',
            contactType: 'customer service',
            email: data.email || '',
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'CN',
            addressLocality: data.address || '',
          },
        };

      case 'WebSite':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: data.name || '企业官网',
          url: baseUrl,
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${baseUrl}/search?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        };

      case 'BreadcrumbList':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: data.items.map((item: any, index: number) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
          })),
        };

      case 'Article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.headline,
          description: data.description,
          image: data.image,
          datePublished: data.datePublished,
          dateModified: data.dateModified || data.datePublished,
          author: {
            '@type': 'Organization',
            name: data.author || '企业官网',
          },
          publisher: {
            '@type': 'Organization',
            name: data.publisher || '企业官网',
            logo: {
              '@type': 'ImageObject',
              url: data.publisherLogo || `${baseUrl}/logo.png`,
            },
          },
        };

      case 'Product':
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: data.name,
          description: data.description,
          image: data.image,
          brand: {
            '@type': 'Brand',
            name: data.brand || '企业官网',
          },
          offers: {
            '@type': 'Offer',
            url: data.url,
            priceCurrency: 'CNY',
            availability: 'https://schema.org/InStock',
          },
        };

      default:
        return null;
    }
  };

  const structuredData = getStructuredData();

  if (!structuredData) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}

